/**
 * Centralized route paths for type-safe navigation.
 * Use these constants instead of hardcoded strings.
 */
export const paths = {
  // Public routes
  root: '/',
  login: '/login',
  register: '/register',
  signup: '/signup',
  resetPassword: '/reset-password',
  kitchenSink: '/kitchen-sink',
  accountSetup: (token: string) => `/account-setup/${token}`,
  organizationAccept: (orgId: string) =>
    `/organizations/${orgId}/accept-invitation`,
  share: {
    // The share token is the only identifier — it resolves the draft and is the
    // entire access credential. `/d/` namespaces draft links, leaving room for
    // other shareable subjects later without colliding on a bare `:token`.
    draft: (shareToken: string) => `/share/d/${shareToken}`,
  },

  // Protected routes
  home: '/home',
  homeWithToken: (token: string) => `/home/${token}`,
  onboarding: '/onboarding',
  createOrganization: '/create-organization',
  inviteOrgMembers: '/invite-org-members',
  selectPlan: '/select-plan',
  myteam: '/myteam',
  updates: '/updates',
  notifications: '/notifications',
  scheduled: '/scheduled',
  search: '/search',

  // Taskboard routes. `board` keys off `task_board.slug`, which is globally
  // unique on the backend (collisions get a -1/-2 suffix), so a slug alone
  // identifies a board without needing the channel in the URL.
  tasks: {
    root: '/tasks',
    board: (slug: string) => `/tasks/${slug}`,
    // Read-only archive for one board. Board-scoped, mirroring the API:
    // `GET .../task-board/:boardId/tasks?archived=true` has no cross-board form.
    boardArchive: (slug: string) => `/tasks/${slug}/archive`,
    // Board creation is a route so it can be linked from the sidebar and
    // deep-linked. A board slugged "new" would shadow it — the backend allows
    // it, but it's not worth a route-shape workaround.
    newBoard: '/tasks/new',
  },

  // Projects routes
  projects: {
    root: '/projects',
    videos: '/projects/videos',
    shorts: '/projects/shorts',
    audio: '/projects/audio',
    thumbnails: '/projects/thumbnails',
    documents: '/projects/documents',
    videosFolder: (folderId: string) => `/projects/videos/folder/${folderId}`,
    shortsFolder: (folderId: string) => `/projects/shorts/folder/${folderId}`,
    audioFolder: (folderId: string) => `/projects/audio/folder/${folderId}`,
    thumbnailsFolder: (folderId: string) =>
      `/projects/thumbnails/folder/${folderId}`,
    documentsFolder: (folderId: string) =>
      `/projects/documents/folder/${folderId}`,
    detail: (revisionId: string) => `/projects/${revisionId}`,
    draft: (revisionId: string, draftId: string) =>
      `/projects/${revisionId}/draft/${draftId}`,
    restore: '/projects/restore',
  },

  // Dashboard routes
  dashboard: {
    root: '/dashboard',
    updates: '/dashboard/updates',
  },

  // Checkout routes
  checkout: (planId: string) => `/checkout/${planId}`,
  checkoutSuccess: '/checkout/success',

  // Help route
  help: '/help',

  // Settings routes
  settings: {
    root: '/settings',
    workspace: '/settings/workspace',
    usage: '/settings/usage',
    members: '/settings/members',
    billing: '/settings/billing',
    channels: '/settings/channels',
    channelDetail: (channelId: string) => `/settings/channels/${channelId}`,
    channelDetails: (channelId: string) =>
      `/settings/channels/${channelId}/details`,
    channelMembers: (channelId: string) =>
      `/settings/channels/${channelId}/members`,
    profile: '/settings/profile',
    security: '/settings/security',
    account: '/settings/account',
    roles: '/settings/roles',
  },
} as const;
